/**
 * Admin · Permissions — read-only role capability matrix.
 *
 * Phase 1: visual reference of the 8 roles × 9 core capabilities. The backend
 * currently honors 3 roles internally (parent/caregiver/admin); the matrix
 * shows the intent for the expanded role model coming in Phase 2.
 */
import { Shield, Check, Minus } from "lucide-react";
import { AdminHeader } from "./_shared";

const ROLES = [
  { id: "super_admin", label: "Super admin", note: "Multi-center ownership" },
  { id: "center_director", label: "Center director", note: "Single-center owner" },
  { id: "admin", label: "Admin", note: "Workspace admin (current)" },
  { id: "teacher", label: "Teacher", note: "Lead caregiver" },
  { id: "assistant_teacher", label: "Assistant teacher", note: "Caregiver" },
  { id: "parent", label: "Parent", note: "Family member" },
  { id: "child_interface", label: "Child interface", note: "Kiosk session" },
  { id: "auditor", label: "Auditor", note: "Read-only oversight" },
];

const CAPABILITIES = [
  "View overview",
  "Manage children",
  "Manage staff",
  "Send messages",
  "Approve whereabouts",
  "Sign in / out",
  "View audit log",
  "Export reports",
  "Manage billing",
];

const MATRIX = {
  super_admin:       ["full", "full", "full", "full", "full", "full", "full", "full", "full"],
  center_director:   ["full", "full", "full", "full", "full", "full", "full", "full", "full"],
  admin:             ["full", "full", "full", "full", "full", "full", "full", "full", "read"],
  teacher:           ["full", "read", "read", "full", "full", "full", "none", "none", "none"],
  assistant_teacher: ["read", "none", "none", "read", "full", "full", "none", "none", "none"],
  parent:            ["none", "none", "none", "reply", "none", "none", "none", "none", "none"],
  child_interface:   ["none", "none", "none", "none", "tap",  "none", "none", "none", "none"],
  auditor:           ["read", "read", "read", "read", "read", "read", "full", "read", "none"],
};

function Cell({ value }) {
  if (value === "full")
    return (
      <span
        className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#132619] text-white"
        title="Full access"
      >
        <Check size={12} strokeWidth={1.8} />
      </span>
    );
  if (value === "read")
    return (
      <span
        className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#E8E2D5] text-[#6B7280]"
        title="Read only"
      >
        <Minus size={12} strokeWidth={1.8} />
      </span>
    );
  if (value === "reply")
    return (
      <span className="text-[10px] tracking-[0.18em] uppercase text-[#1F3A2A] font-medium">
        Reply only
      </span>
    );
  if (value === "tap")
    return (
      <span className="text-[10px] tracking-[0.18em] uppercase text-[#8A4A3B] font-medium">
        Tap only
      </span>
    );
  return (
    <span className="text-[#A3A3A3]" title="No access">
      —
    </span>
  );
}

export default function AdminPermissions() {
  return (
    <div data-testid="admin-permissions-screen">
      <AdminHeader
        eyebrow="Permissions"
        title="What each role may quietly do"
        action={
          <span className="trust-pill">
            <Shield size={11} strokeWidth={1.5} /> 8 roles
          </span>
        }
      />
      <p className="mt-2 text-sm text-[#6B7280] max-w-2xl">
        The matrix shows the intended access for every role. The platform
        currently honors three internal roles (Admin, Caregiver, Parent); the
        full set rolls out in a coming iteration.
      </p>

      <div
        data-testid="admin-permissions-matrix"
        className="mt-6 rounded-3xl bg-white border hairline overflow-hidden overflow-x-auto"
      >
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FC]/60">
            <tr>
              <th className="text-left text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-medium px-5 py-3">
                Role
              </th>
              {CAPABILITIES.map((c) => (
                <th
                  key={c}
                  className="text-center text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-medium px-2 py-3 whitespace-nowrap"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLES.map((r) => (
              <tr
                key={r.id}
                data-testid={`admin-permissions-row-${r.id}`}
                className="border-t border-[#171717]/6"
              >
                <td className="px-5 py-4 whitespace-nowrap">
                  <div className="font-serif text-base text-[#1A1B2E]">
                    {r.label}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{r.note}</div>
                </td>
                {MATRIX[r.id].map((v, i) => (
                  <td key={i} className="px-2 py-4 text-center">
                    <Cell value={v} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
